import { startTransition, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useTran } from "@/hooks";
import DisclosureTargetRow from "./DisclosureTargetRow";
import DisclosureRowsSkeleton from "./skeleton/DisclosureRowsSkeleton";

const CATEGORY_STYLES = {
  General: {
    bg: "bg-purple-100",
    text: "text-purple-700",
    badge: "bg-purple-600",
  },
  Environment: {
    bg: "bg-emerald-100",
    text: "text-emerald-700",
    badge: "bg-emerald-600",
  },
  Social: { bg: "bg-blue-100", text: "text-blue-700", badge: "bg-blue-600" },
  Governance: {
    bg: "bg-amber-100",
    text: "text-amber-700",
    badge: "bg-amber-600",
  },
};

const CategoryGroup = ({
  category,
  disclosures = [],
  getCardyearLabel,
  getReportingPeriodMonths,
}) => {
  const { t } = useTran();
  const [expanded, setExpanded] = useState(false);
  const [rowsReady, setRowsReady] = useState(false);
  const frameRef = useRef(null);
  const style = CATEGORY_STYLES[category] || CATEGORY_STYLES.Environment;
  const initial = category?.charAt(0)?.toUpperCase() || "?";

  useEffect(() => {
    if (!expanded) return undefined;

    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      startTransition(() => {
        setRowsReady(true);
      });
    });

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [expanded]);

  const skeletonCount = Math.min(Math.max(disclosures.length, 1), 3);

  const handleToggle = () => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    if (expanded) {
      setRowsReady(false);
      setExpanded(false);
      return;
    }

    setRowsReady(false);
    setExpanded(true);
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Category header */}
      <button
        type="button"
        onClick={handleToggle}
        className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left hover:bg-muted/40 transition"
      >
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${style.bg} ${style.text}`}
        >
          {initial}
        </span>

        <div className="flex flex-1 flex-col">
          <span className="text-sm font-semibold text-foreground">
            {t(category)}
          </span>
          <span className="text-xs text-muted-foreground">
            {disclosures.length} {t("disclosure(s)")}
          </span>
        </div>

        <Badge className={`${style.badge} text-white text-xs`}>
          {disclosures.length}
        </Badge>

        {expanded ? (
          <ChevronDown className="h-5 w-5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        )}
      </button>

      {/* Disclosure rows */}
      {expanded && disclosures.length > 0 ? (
        rowsReady ? (
          <div className="flex flex-col gap-3 pl-2">
            {disclosures.map((d) => (
              <DisclosureTargetRow
                key={`${d._id || d.code}-${d.masterDataYear ?? "na"}-${
                  d.framework || ""
                }`}
                disclosure={d}
                getCardyearLabel={getCardyearLabel}
                getReportingPeriodMonths={getReportingPeriodMonths}
              />
            ))}
          </div>
        ) : (
          <DisclosureRowsSkeleton count={skeletonCount} />
        )
      ) : null}
    </div>
  );
};

export default CategoryGroup;
